/**
 * โรงคลัง: the player's store, one per character, shared by every town's keeper (data/warehouse.ts).
 *
 * Per character, not per account: saves are per character slot (apps/client0/src/storage.ts) and rolled items live in
 * the character's own `rolls` (ids `roll-N` are only unique within one character), so the store sits in
 * `PlayerProgress.warehouse` next to the bag. An account-wide store waits for server accounts.
 *
 * Talking to a keeper (an NPC titled `WAREHOUSE.keeperTitle`) opens it (`warehouse` event `open`); walking out of reach,
 * dying or `whClose` closes it. Every command checks a keeper is in reach, so a server World validates them the same.
 * Slots: one entry per slot; a plain item stacks up to `stackMax`, a rolled item is a slot of its own.
 */
import { WAREHOUSE, WAREHOUSE_TEXT } from '../data/warehouse';
import { BAG_SIZE, itemOf, type ItemRoll } from '../data/items';
import type { Entity, PlayerCommand, WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

export interface StoreSlot {
  id: string;
  n: number;
}

export interface WarehouseSave {
  /** Slots the store has (grows later, up to `WAREHOUSE.maxSlots`). */
  size: number;
  /** Filled slots, in order (empty slots are not kept). */
  items: StoreSlot[];
  /** ตำลึง kept in the store. */
  gold: number;
}

declare module '../progress' {
  interface PlayerProgress {
    /** โรงคลัง (systems/warehouse.ts); absent until first used. */
    warehouse?: WarehouseSave;
  }
}

export type WarehouseCommand =
  /** Put `n` (default 1) unworn copies of a bag item into the store. */
  | { t: 'whDeposit'; id: string; n?: number }
  /** Take `n` (default the whole slot) out of a store slot into the bag. */
  | { t: 'whWithdraw'; slot: number; n?: number }
  /** Split `n` off a store stack into a new slot. */
  | { t: 'whSplit'; slot: number; n: number }
  /** Merge the stacks of the same item and sort the store. */
  | { t: 'whTidy' }
  /** Move ตำลึง: positive puts them in the store, negative takes them out. */
  | { t: 'whGold'; gold: number }
  | { t: 'whClose' };

export type WarehouseEvent = { type: 'warehouse'; what: 'open' | 'close' | 'changed'; keeper?: number };

export const newWarehouse = (): WarehouseSave => ({ size: WAREHOUSE.slots, items: [], gold: 0 });

const int = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? Math.floor(v) : d);

/** A save's store, repaired against current items: unknown items dropped, stacks clamped, rolled items single. */
export function parseWarehouse(raw: unknown, rolls: Readonly<Record<string, ItemRoll>>): WarehouseSave {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<WarehouseSave>;
  const size = Math.min(WAREHOUSE.maxSlots, Math.max(WAREHOUSE.slots, int(r.size, WAREHOUSE.slots)));
  const items: StoreSlot[] = [];
  const seenRoll = new Set<string>();
  for (const s of Array.isArray(r.items) ? r.items : []) {
    if (items.length >= size) break;
    if (!s || typeof s !== 'object' || typeof s.id !== 'string' || !itemOf(rolls, s.id)) continue;
    const rolled = !!rolls[s.id];
    if (rolled && seenRoll.has(s.id)) continue;
    if (rolled) seenRoll.add(s.id);
    const n = rolled ? 1 : Math.min(WAREHOUSE.stackMax, int(s.n, 1));
    if (n > 0) items.push({ id: s.id, n });
  }
  return { size, items, gold: Math.min(WAREHOUSE.goldMax, Math.max(0, int(r.gold, 0))) };
}

/** Whether an NPC keeps a warehouse. */
export const isKeeper = (e: Entity | undefined): boolean =>
  !!e && !e.dead && e.title === WAREHOUSE.keeperTitle && e.kind !== 'player';

const SYSTEMS = new WeakMap<object, WarehouseSystem>();
/** The world's warehouse system (the client's window reads it). */
export const warehouseOf = (w: object): WarehouseSystem | null => SYSTEMS.get(w) ?? null;

export class WarehouseSystem implements WorldSystem {
  readonly name = 'warehouse';
  /** The keeper whose store is open, or null. */
  keeper: number | null = null;

  constructor(private readonly h: SystemHost) {}

  setup(): void {
    SYSTEMS.set(this.h.world, this);
  }

  /** The player's store (made on first use). */
  store(): WarehouseSave {
    const pr = this.h.world.progress;
    return (pr.warehouse ??= newWarehouse());
  }

  /** The store as it is, without making one (the window reads this). */
  view(): WarehouseSave {
    return this.h.world.progress.warehouse ?? newWarehouse();
  }

  get isOpen(): boolean {
    return this.keeper !== null;
  }

  private inReach(e: Entity | undefined): boolean {
    const p = this.h.world.player;
    return !!e && isKeeper(e) && !p.dead && Math.hypot(e.x - p.x, e.y - p.y) <= WAREHOUSE.reach;
  }

  /** A keeper in reach of the player (the open one first), or null. */
  private nearKeeper(): Entity | null {
    const w = this.h.world;
    const open = this.keeper !== null ? w.entities.get(this.keeper) : undefined;
    if (this.inReach(open)) return open!;
    for (const e of w.entities.values()) if (this.inReach(e)) return e;
    return null;
  }

  private open(e: Entity): void {
    this.keeper = e.id;
    this.store();
    this.h.emit({ type: 'warehouse', what: 'open', keeper: e.id });
  }

  private close(): void {
    if (this.keeper === null) return;
    this.keeper = null;
    this.h.emit({ type: 'warehouse', what: 'close' });
  }

  private changed(): void {
    this.h.emit({ type: 'warehouse', what: 'changed' });
    this.h.emit({ type: 'progressChanged' });
  }

  /** The keeper's greeting (any talk, outdoors or in a room) opens the store. */
  observe(e: WorldEvent): void {
    if (e.type !== 'dialog') return;
    const npc = this.h.world.entities.get(e.npcId);
    if (this.inReach(npc)) this.open(npc!);
  }

  step(): void {
    if (this.keeper === null) return;
    if (!this.inReach(this.h.world.entities.get(this.keeper))) this.close();
  }

  readonly commands = {
    whDeposit: (c: { id: string; n?: number }) => this.guard(() => this.deposit(c.id, c.n)),
    whWithdraw: (c: { slot: number; n?: number }) => this.guard(() => this.withdraw(c.slot, c.n)),
    whSplit: (c: { slot: number; n: number }) => this.guard(() => this.split(c.slot, c.n)),
    whTidy: () => this.guard(() => this.tidy()),
    whGold: (c: { gold: number }) => this.guard(() => this.gold(c.gold)),
    whClose: () => this.close(),
  };

  /** Runs a store action only with a keeper in reach; true from the action emits the change. */
  private guard(act: () => boolean): void {
    if (!this.nearKeeper()) return this.h.notice(WAREHOUSE_TEXT.far);
    if (act()) this.changed();
  }

  /** Copies of `id` in the bag that are not worn. */
  private loose(id: string): number {
    const pr = this.h.world.progress;
    const have = pr.inventory.reduce((k, x) => k + (x === id ? 1 : 0), 0);
    const worn = Object.values(pr.equipment).includes(id) ? 1 : 0;
    return have - worn;
  }

  private deposit(id: unknown, nRaw: unknown): boolean {
    const pr = this.h.world.progress;
    if (typeof id !== 'string' || !itemOf(pr.rolls, id)) return false;
    const loose = this.loose(id);
    if (loose <= 0) {
      if (pr.inventory.includes(id)) this.h.notice(WAREHOUSE_TEXT.worn);
      return false;
    }
    const st = this.store();
    const rolled = !!pr.rolls[id];
    let want = Math.min(loose, Math.max(1, int(nRaw, 1)));
    let moved = 0;
    while (want > 0) {
      let slot = rolled ? undefined : st.items.find((s) => s.id === id && s.n < WAREHOUSE.stackMax);
      if (!slot) {
        if (st.items.length >= st.size) break;
        slot = { id, n: 0 };
        st.items.push(slot);
      }
      const k = rolled ? 1 : Math.min(want, WAREHOUSE.stackMax - slot.n);
      slot.n += k;
      want -= k;
      moved += k;
    }
    if (!moved) {
      this.h.notice(WAREHOUSE_TEXT.full);
      return false;
    }
    // Take the copies out of the bag, last ones first (moved <= loose, so a worn copy stays).
    for (let left = moved, i = pr.inventory.length - 1; i >= 0 && left > 0; i--)
      if (pr.inventory[i] === id) {
        pr.inventory.splice(i, 1);
        left--;
      }
    if (want > 0) this.h.notice(WAREHOUSE_TEXT.full);
    return true;
  }

  private withdraw(slotRaw: unknown, nRaw: unknown): boolean {
    const pr = this.h.world.progress;
    const st = this.store();
    const i = int(slotRaw, -1);
    const s = st.items[i];
    if (!s) return false;
    const room = BAG_SIZE - pr.inventory.length;
    if (room <= 0) {
      this.h.notice(WAREHOUSE_TEXT.bagFull);
      return false;
    }
    const want = Math.min(s.n, Math.max(1, int(nRaw, s.n)));
    const k = Math.min(want, room);
    for (let j = 0; j < k; j++) pr.inventory.push(s.id);
    s.n -= k;
    if (s.n <= 0) st.items.splice(i, 1);
    if (k < want) this.h.notice(WAREHOUSE_TEXT.bagFull);
    return true;
  }

  private split(slotRaw: unknown, nRaw: unknown): boolean {
    const st = this.store();
    const s = st.items[int(slotRaw, -1)];
    const n = int(nRaw, 0);
    if (!s || n <= 0 || n >= s.n) return false;
    if (st.items.length >= st.size) {
      this.h.notice(WAREHOUSE_TEXT.full);
      return false;
    }
    s.n -= n;
    st.items.splice(st.items.indexOf(s) + 1, 0, { id: s.id, n });
    return true;
  }

  private tidy(): boolean {
    const st = this.store();
    const rolls = this.h.world.progress.rolls;
    const totals = new Map<string, number>();
    const single: StoreSlot[] = [];
    for (const s of st.items) {
      if (rolls[s.id]) single.push(s);
      else totals.set(s.id, (totals.get(s.id) ?? 0) + s.n);
    }
    const out: StoreSlot[] = [];
    for (const id of [...totals.keys()].sort()) {
      let left = totals.get(id)!;
      while (left > 0) {
        const n = Math.min(left, WAREHOUSE.stackMax);
        out.push({ id, n });
        left -= n;
      }
    }
    out.push(...single.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)));
    st.items = out;
    return true;
  }

  private gold(raw: unknown): boolean {
    const pr = this.h.world.progress;
    const v = int(raw, 0);
    if (!v) return false;
    const st = this.store();
    if (v > 0) {
      const k = Math.min(v, pr.gold, WAREHOUSE.goldMax - st.gold);
      if (k <= 0) {
        this.h.notice(pr.gold <= 0 ? WAREHOUSE_TEXT.noGold : WAREHOUSE_TEXT.full);
        return false;
      }
      pr.gold -= k;
      st.gold += k;
    } else {
      const k = Math.min(-v, st.gold);
      if (k <= 0) {
        this.h.notice(WAREHOUSE_TEXT.noGold);
        return false;
      }
      st.gold -= k;
      pr.gold += k;
    }
    return true;
  }
}

/** Every command of the store (for the net table and tests). */
export const WAREHOUSE_COMMANDS: readonly PlayerCommand['t'][] = ['whDeposit', 'whWithdraw', 'whSplit', 'whTidy', 'whGold', 'whClose'];
